import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import StarRating from "../components/StarRating";
import { Search } from "lucide-react";
import { COMPANY_LIST } from "../data/companies";

export default function CompanyListPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("All categories");

  useEffect(() => {
    // Keep state in sync if user lands here with ?q= from another page
    setQ(params.get("q") || "");
  }, [params]);

  const categories = useMemo(() => {
    const set = new Set(COMPANY_LIST.map((c) => c.category));
    return ["All categories", ...Array.from(set).sort()];
  }, []);

  const list = useMemo(() => {
    return COMPANY_LIST
      .filter((c) =>
        (cat === "All categories" || c.category === cat) &&
        (!q || c.name.toLowerCase().includes(q.toLowerCase()))
      )
      .sort((a, b) => b.totalReviews - a.totalReviews);
  }, [q, cat]);

  const onChangeQ = (val) => {
    setQ(val);
    if (val) setParams({ q: val }, { replace: true });
    else setParams({}, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f5f7f8] font-[Inter,system-ui,-apple-system,sans-serif] flex flex-col">
      <Header />
      <Breadcrumb trail={[{ label: "Home", to: "/" }, { label: "Browse Companies" }]}/>

      <section className="bg-gradient-to-b from-[#007a8a] to-[#005f6b] text-white">
        <div className="max-w-[1200px] mx-auto px-4 py-7 md:py-10">
          <h1 className="text-2xl md:text-4xl font-bold">Browse UK Companies</h1>
          <p className="mt-2 text-white/85 max-w-2xl text-sm md:text-base">
            Honest customer reviews of UK companies across logistics, retail, telecoms, utilities and more.
            Click any company to read full reviews and see customer-support details.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex-1 flex items-center bg-white text-gray-800 rounded-md overflow-hidden">
              <Search size={16} className="mx-3 text-gray-400 shrink-0"/>
              <input
                value={q}
                onChange={(e) => onChangeQ(e.target.value)}
                placeholder="Search by company name…"
                className="flex-1 min-w-0 py-2.5 text-sm outline-none"
              />
            </div>
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="bg-white text-gray-800 rounded-md px-3 py-2.5 text-sm outline-none"
            >
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </section>

      <main className="flex-1">
        <div className="max-w-[1200px] mx-auto px-4 py-8">
          <div className="text-sm text-gray-600 mb-4">
            {list.length} {list.length === 1 ? "company" : "companies"} found
            {q && <> matching "<span className="font-semibold text-gray-800">{q}</span>"</>}
          </div>
          {list.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-md p-10 text-center text-gray-500">
              <p>No companies match "<span className="font-semibold">{q}</span>".</p>
              <button onClick={() => onChangeQ("")} className="mt-3 text-[#007a8a] hover:underline font-medium">
                Clear search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((c) => (
                <Link
                  key={c.slug}
                  to={`/company/${c.slug}`}
                  className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-md hover:border-[#007a8a]/40 transition-all flex items-center gap-3"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                    style={{ backgroundColor: c.color }}
                  >
                    {c.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{c.name}</div>
                    <div className="text-xs text-gray-500 truncate">{c.category}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <StarRating value={c.rating} size={12}/>
                      <span className="text-xs text-gray-500">
                        {c.totalReviews.toLocaleString()} reviews
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
